import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'

import { computed, ref } from 'vue'

import {
  CLUB_SELF_REGISTRATION_DISABLED,
  CLUB_SELECTOR_PATH,
} from '@/features/auth/clubSelfRegistrationConfig.js'
import { decideRedirectionPointByRole } from '@/features/auth/decideRedirectionPointByRole.js'
import { useLoginStore } from '@/features/auth/views/login/useLoginStore.js'
import { useSocialLoginStore } from '@/features/auth/views/login/useSocialLoginStore.js'
import { useClubRouter } from '@/features/router/useClubRouer.js'

const STEPS = {
  LOGIN: 'login',
  ORG_DETAILS: 'orgDetails',
  ORG_STRUCTURE: 'orgStructure',
  PUBLIC_CONTACT: 'publicContact',
  ORG_TYPE: 'orgType',
  TERMINOLOGY: 'terminology',
  BRANDING: 'branding',
  BACKGROUND: 'background',
  COMPONENTS: 'components',
  SUMMARY: 'summary',
}

export const clubRegisterStepRouteMap = {
  [STEPS.LOGIN]: 'login',
  [STEPS.ORG_DETAILS]: 'details',
  [STEPS.ORG_STRUCTURE]: 'structure',
  [STEPS.PUBLIC_CONTACT]: 'contact',
  [STEPS.ORG_TYPE]: 'type',
  [STEPS.TERMINOLOGY]: 'terminology',
  [STEPS.BRANDING]: 'branding',
  [STEPS.BACKGROUND]: 'background',
  [STEPS.COMPONENTS]: 'components',
  [STEPS.SUMMARY]: 'summary',
}

const AUTH_FLOW = {
  LOGIN: 'login',
  REGISTER: 'register',
}

export const useClubRegisterStore = defineStore('clubRegisterStore', function () {
  const currentStep = ref(STEPS.LOGIN)
  const loginStore = useLoginStore()
  const socialLoginStore = useSocialLoginStore()
  const route = useRoute()
  const clubIdExternal = ref(null)
  const clubId = computed(() => clubIdExternal.value || route.query.clubId)

  const router = useClubRouter()

  const currentAuthFlow = ref(AUTH_FLOW.REGISTER)

  function setCurrentStep(step, newClubId) {
    if (CLUB_SELF_REGISTRATION_DISABLED) {
      router.push(CLUB_SELECTOR_PATH)
      return
    }

    const query = route.query
    let path = `/club-register/${clubRegisterStepRouteMap[step]}`

    if (step === STEPS.LOGIN) {
      path = `/club-register`
    }

    if (!newClubId) {
      console.trace('No currentTenantId, redirecting to club selector')
      router.push('/auth/club-selector')

      return
    }

    router.push({
      path,
      query: {
        ...query,
        clubId: newClubId,
      },
    })
  }

  const isLoginFlow = computed(() => currentAuthFlow.value === AUTH_FLOW.LOGIN)
  const isRegisterFlow = computed(() => currentAuthFlow.value === AUTH_FLOW.REGISTER)

  async function redirectCallbackLogin({ myDetailsResponse }) {
    const firstNotRegistered = (myDetailsResponse?.myTenants || []).find(item => {
      return !item.isRegisteredFinalized
    })

    if (firstNotRegistered) {
      setCurrentStep(STEPS.ORG_DETAILS, firstNotRegistered.tenantId)

      return
    }

    console.log('Registration already completed for this user')
    window.location.href = decideRedirectionPointByRole(myDetailsResponse.type)
  }

  function _loginEmailEnterUserIsNotExistCallback() {
    currentAuthFlow.value = AUTH_FLOW.REGISTER
  }

  function setAuthFlow(flow) {
    currentAuthFlow.value = flow
  }

  function setListeners() {
    loginStore.setRedirectCallback(redirectCallbackLogin)
    loginStore.setLoginEmailEnterUserIsNotExistCallback(_loginEmailEnterUserIsNotExistCallback)
    socialLoginStore.setLoginEmailEnterUserIsNotExistCallback(
      _loginEmailEnterUserIsNotExistCallback,
    )
  }

  function setClubIdExternal(newClubId) {
    clubIdExternal.value = newClubId
  }

  return {
    setCurrentStep,
    currentStep,
    STEPS,
    clubId,
    setClubIdExternal,

    currentAuthFlow,
    isLoginFlow,
    isRegisterFlow,

    setAuthFlow,
    AUTH_FLOW,
    setListeners,
  }
})
